"use client"

import Script from "next/script"
import {
  createContext,
  useContext,
  useState,
  useEffect,
  Suspense,
} from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { env } from "../types/env"

type PostHogClient = {
  capture: (event: string, properties?: Record<string, unknown>) => void
  init: (apiKey: string, options: Record<string, unknown>) => void
  __loaded?: boolean
}

const PostHogContext = createContext<PostHogClient | null>(null)

export const usePostHog = () => useContext(PostHogContext)

const isProductionEnvironment = process.env.NODE_ENV === "production"

const allowedProductionHosts = new Set([
  "www.automateanalyzeinnovate.com",
  "automateanalyzeinnovate.com",
])

const shouldLoadAnalytics = () => {
  if (!isProductionEnvironment) {
    return false
  }

  if (typeof window === "undefined") {
    return false
  }

  return Boolean(env.posthog && allowedProductionHosts.has(window.location.hostname))
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [posthog, setPosthog] = useState<PostHogClient | null>(null)
  const [shouldLoadPosthog, setShouldLoadPosthog] = useState<boolean>(false)

  useEffect(() => {
    setShouldLoadPosthog(shouldLoadAnalytics())
  }, [])

  const handleLoad = () => {
    if (!shouldLoadPosthog) {
      return
    }

    const ph = (window as Window & { posthog?: PostHogClient }).posthog
    if (ph && !ph.__loaded && env.posthog) {
      ph.init(env.posthog, {
        api_host: "/ingest",
        ui_host: "https://us.posthog.com",
        capture_pageview: false,
        capture_pageleave: true,
        debug: process.env.NODE_ENV === "development",
      })
    }
    if (ph) {
      setPosthog(ph)
    }
  }

  return (
    <PostHogContext.Provider value={shouldLoadPosthog ? posthog : null}>
      {shouldLoadPosthog && (
        <Script src="/ingest/static/array.js" strategy="afterInteractive" onLoad={handleLoad} />
      )}
      {shouldLoadPosthog && <SuspendedPostHogPageView />}
      {children}
    </PostHogContext.Provider>
  )
}

function PostHogPageView() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const posthogClient = usePostHog()

  useEffect(() => {
    if (pathname && posthogClient) {
      let url = window.origin + pathname
      const search = searchParams.toString()
      if (search) {
        url += "?" + search
      }
      posthogClient.capture("$pageview", { $current_url: url })
    }
  }, [pathname, searchParams, posthogClient])

  return null
}

function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  )
}
